import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { FieldStatusDto, SourcingEventDto } from "../../../../../../autogen/bff-api";
import { TipTap, isTiptapContentEmpty } from "../../../../../../common/input/TipTap/TipTap";
import type {
  ReadinessLookup,
  KnownFieldKey,
} from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import {
  allowsNegotiation,
  isDpsScheme,
  isQualificationScheme,
  isTwoStageProcedure,
  offersAwardOnInitialTenders,
} from "../../utils/procedure";
import { requiresProcedureJustification } from "../../utils/publicProcurementOptions";
import {
  followsDesignContestBelowEeaTier,
  isWithoutCompetition,
  negotiationCaseProvision,
  noCompetitionProvision,
} from "../../utils/noCompetition";
import { isNegotiationWithoutNotice } from "../../utils/negotiationWithoutNotice";
import { NegotiationWithoutNoticeSummary } from "./NegotiationWithoutNoticeSummary";
import { DesignContestSummary } from "./DesignContestSummary";
import { isDesignContest } from "../../utils/designContest";
import { useDisplayDate } from "../../../../../../common/formatting/useDisplayDate";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { isFoaPartII } from "../../utils/foaPartII";
import { SummarySection } from "./SummarySection";
import { SchemeCompetitionInvitees } from "./SchemeCompetitionInvitees";
import { QualificationSchemeInvitees } from "../qualificationScheme/QualificationSchemeInvitees";

export const FIELD_KEYS = [
  "ProcedureTypeSet",
  "ProcedureSupportedByProfile",
  "ProcedureRequiresEeaTier",
  "TenderCompetitionRequiresPartII",
  "CandidateMinimumAboveFloor",
  "CandidateMaximumNotBelowMinimum",
  "CandidateSelectionCriteriaPresent",
  "ProcedureJustificationPresent",
  "DialoguePlanStated",
  "DialogueDescriptionPresent",
  "DpsUsageSet",
  "DpsValidityPresent",
  "DpsValidityOrdered",
  "QualificationSchemeValidityPresent",
  "QualificationSchemeValidityOrdered",
  "ProcedureRequiresUtilitiesRegulation",
  "PriorInformationCallOffered",
  "PlannedInvitationWithinNoticeWindow",
  "TenderValidityMonthsSet",
  "SubmissionLanguagesSet",
  "SubmissionLanguagesValid",
  "WithoutCompetitionAtEeaTier",
  "NoCompetitionConditionSet",
  "NoCompetitionRationalePresent",
  "NoCompetitionConditionInRegulation",
  "NoCompetitionOriginalContractPresent",
  "NoCompetitionRepetitionForeseen",
  "NoCompetitionNegotiationCaseSet",
  "NoCompetitionWindingUpCaseSet",
  "NegotiationWithoutNoticeAtEeaTier",
  "NegotiationWithoutNoticeCaseSet",
  "NegotiationWithoutNoticeRationalePresent",
  "NegotiationWithoutNoticePrecedingCompetitionSet",
  "NegotiationWithoutNoticeBasisUnchanged",
  "NegotiationWithoutNoticeHasInvitees",
  "DesignContestWithinPartV",
  "DesignContestClimateConsiderationStated",
  "DesignContestFollowUpStated",
  "DesignContestJuryCriteriaPresent",
  "DesignContestJuryPresent",
  "DesignContestJuryIndependent",
  "DesignContestJuryQualifiedShare",
  "DesignContestPrizesDescribed",
] as const satisfies readonly KnownFieldKey[];

const LANGUAGE_LABELS: Record<string, string> = {
  NOR: "Norwegian",
  ENG: "English",
};

interface ProcedureSectionProps {
  event: SourcingEventDto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const ProcedureSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
}: ProcedureSectionProps) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const candidateLimits = publicContext?.candidateLimits;
  const validityMonths = publicContext?.eFormsNoticeData?.tenderValidityMonths;
  const submissionLanguages = publicContext?.eFormsNoticeData?.submissionLanguages ?? [];
  const withoutCompetition = isWithoutCompetition(publicContext?.procedureType);
  const noCompetition = publicContext?.noCompetitionJustification;
  const schemeCompetition = publicContext?.qualificationSchemeCompetition;

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  return (
    <SummarySection
      title={t("Procedure")}
      editTarget={{ kind: "wizard-step", step: "procedure" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryFieldGroup>
        <SummaryField
          label={t("Procedure type")}
          value={
            publicContext?.procedureType
              ? t(publicContext.procedureType)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("ProcedureTypeSet")}
          extraStatuses={[
            readinessFor("ProcedureSupportedByProfile"),
            readinessFor("ProcedureRequiresEeaTier"),
            readinessFor("TenderCompetitionRequiresPartII"),
            readinessFor("WithoutCompetitionAtEeaTier"),
            readinessFor("NegotiationWithoutNoticeAtEeaTier"),
          ]}
        />
        {isTwoStageProcedure(publicContext?.procedureType) && (
          <SummaryField
            label={schemeCompetition ? t("Selection of suppliers") : t("Limit on invited suppliers")}
            value={
              candidateLimits ? (
                <Box>
                  <Text>
                    {`${candidateLimits.minCandidates}${
                      candidateLimits.maxCandidates != null
                        ? `–${candidateLimits.maxCandidates}`
                        : "+"
                    }`}
                  </Text>
                  {isTiptapContentEmpty(candidateLimits.selectionCriteria) ? (
                    <EmptyValue text={t("Not set")} />
                  ) : (
                    <Box mt="1" fontSize="sm" color="smMuted">
                      <TipTap
                        content={candidateLimits.selectionCriteria}
                        editable={false}
                        hasBorder={false}
                        padding="0"
                        maxHeight=""
                      />
                    </Box>
                  )}
                </Box>
              ) : schemeCompetition ? (
                <SchemeCompetitionInvitees competition={schemeCompetition} />
              ) : (
                t("All qualified suppliers are invited")
              )
            }
            span="half"
            status={readinessFor("CandidateMinimumAboveFloor")}
            extraStatuses={[
              readinessFor("CandidateMaximumNotBelowMinimum"),
              readinessFor("CandidateSelectionCriteriaPresent"),
            ]}
          />
        )}
        {schemeCompetition && (
          <SummaryField
            label={t("Invited suppliers")}
            value={<QualificationSchemeInvitees eventId={event.id} />}
            span="full"
          />
        )}
        {isFoaPartII(publicContext?.regulation, publicContext?.foaPart) && (
          <SummaryField
            label={t("Dialogue planned")}
            value={
              publicContext?.dialoguePlanned == null ? undefined : publicContext.dialoguePlanned ? (
                <Box>
                  <Text>{t("Yes")}</Text>
                  {publicContext.dialogueDescription && !isTiptapContentEmpty(publicContext.dialogueDescription) && (
                    <Box mt="1" fontSize="sm" color="smMuted">
                      <TipTap
                        content={publicContext.dialogueDescription}
                        editable={false}
                        hasBorder={false}
                        padding="0"
                        maxHeight=""
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                t("No")
              )
            }
            span="full"
            status={readinessFor("DialoguePlanStated")}
            extraStatuses={[readinessFor("DialogueDescriptionPresent")]}
          />
        )}
        {allowsNegotiation(publicContext?.procedureType) &&
          !isNegotiationWithoutNotice(publicContext?.procedureType) &&
          requiresProcedureJustification(publicContext?.regulation) && (
          <SummaryField
            label={t("Grounds for negotiation (FOA § 13-2)")}
            value={
              publicContext?.procedureJustification &&
              !isTiptapContentEmpty(publicContext.procedureJustification.rationale) ? (
                <Box>
                  <Text>{t(publicContext.procedureJustification.ground)}</Text>
                  <Box mt="1" fontSize="sm" color="smMuted">
                    <TipTap
                      content={publicContext.procedureJustification.rationale}
                      editable={false}
                      hasBorder={false}
                      padding="0"
                      maxHeight=""
                    />
                  </Box>
                  {publicContext.noNegotiationNecessary === true &&
                    offersAwardOnInitialTenders(publicContext.procedureType) && (
                    <Text mt="1" fontSize="sm" color="smMuted">
                      {t("May be awarded on the initial tenders without negotiation")}
                    </Text>
                  )}
                </Box>
              ) : (
                <EmptyValue text={t("Not set")} />
              )
            }
            span="half"
            status={readinessFor("ProcedureJustificationPresent")}
          />
        )}
        {withoutCompetition && (
          <SummaryField
            label={t("Grounds for a procurement without competition")}
            value={
              noCompetition ? (
                <Box>
                  <Text>{t(noCompetition.condition)}</Text>
                  <Text fontSize="xs" color="smMuted" fontStyle="italic">
                    {noCompetitionProvision(
                      noCompetition.condition,
                      publicContext?.regulation,
                      t,
                      followsDesignContestBelowEeaTier(publicContext),
                    )}
                  </Text>
                  {noCompetition.negotiationImpossibleCase && (
                    <>
                      <Text fontSize="sm" mt="1">{t(noCompetition.negotiationImpossibleCase)}</Text>
                      <Text fontSize="xs" color="smMuted" fontStyle="italic">
                        {negotiationCaseProvision(noCompetition.negotiationImpossibleCase, publicContext?.regulation, t)}
                      </Text>
                    </>
                  )}
                  {noCompetition.windingUpCase && (
                    <Text fontSize="sm" mt="1">{t(noCompetition.windingUpCase)}</Text>
                  )}
                  {!isTiptapContentEmpty(noCompetition.rationale) && (
                    <Box mt="1" fontSize="sm" color="smMuted">
                      <TipTap
                        content={noCompetition.rationale}
                        editable={false}
                        hasBorder={false}
                        padding="0"
                        maxHeight=""
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <EmptyValue text={t("Not set")} />
              )
            }
            span="full"
            status={readinessFor("NoCompetitionConditionSet")}
            extraStatuses={[
              readinessFor("NoCompetitionRationalePresent"),
              readinessFor("NoCompetitionConditionInRegulation"),
              readinessFor("NoCompetitionOriginalContractPresent"),
              readinessFor("NoCompetitionRepetitionForeseen"),
              readinessFor("NoCompetitionNegotiationCaseSet"),
              readinessFor("NoCompetitionWindingUpCaseSet"),
            ]}
          />
        )}
        {isNegotiationWithoutNotice(publicContext?.procedureType) && (
          <SummaryField
            label={t("Grounds for negotiating without a notice")}
            value={<NegotiationWithoutNoticeSummary event={event} />}
            span="full"
            status={readinessFor("NegotiationWithoutNoticeCaseSet")}
            extraStatuses={[
              readinessFor("NegotiationWithoutNoticeRationalePresent"),
              readinessFor("NegotiationWithoutNoticePrecedingCompetitionSet"),
              readinessFor("NegotiationWithoutNoticeBasisUnchanged"),
              readinessFor("NegotiationWithoutNoticeHasInvitees"),
            ]}
          />
        )}
        {isDesignContest(publicContext?.procedureType) && (
          <SummaryField
            label={t("Design contest")}
            value={<DesignContestSummary contest={publicContext?.designContest} />}
            span="full"
            status={readinessFor("DesignContestWithinPartV")}
            extraStatuses={[
              readinessFor("DesignContestClimateConsiderationStated"),
              readinessFor("DesignContestFollowUpStated"),
              readinessFor("DesignContestJuryCriteriaPresent"),
              readinessFor("DesignContestJuryPresent"),
              readinessFor("DesignContestJuryIndependent"),
              readinessFor("DesignContestJuryQualifiedShare"),
              readinessFor("DesignContestPrizesDescribed"),
            ]}
          />
        )}
        {isDpsScheme(publicContext?.procedureType) && (
          <SummaryField
            label={t("The dynamic purchasing system")}
            value={
              publicContext?.dpsScheme ? (
                <Box>
                  <Text>
                    {`${displayDate(publicContext.dpsScheme.validFrom)} – ${displayDate(
                      publicContext.dpsScheme.validTo,
                    )}`}
                  </Text>
                  <Text mt="1" fontSize="sm" color="smMuted">
                    {publicContext.dynamicPurchasingSystem
                      ? t(publicContext.dynamicPurchasingSystem)
                      : t("Not set")}
                  </Text>
                </Box>
              ) : (
                <EmptyValue text={t("Not set")} />
              )
            }
            span="half"
            status={readinessFor("DpsValidityPresent")}
            extraStatuses={[readinessFor("DpsValidityOrdered"), readinessFor("DpsUsageSet")]}
          />
        )}
        {publicContext?.priorInformationCall && (
          <SummaryField
            label={t("Call for competition")}
            value={
              <Text>
                {t("Prior information notice (forsyningsforskriften § 17-2 (2)), invitation planned {{date}}", {
                  date: displayDate(publicContext.priorInformationCall.plannedInvitationAt),
                })}
              </Text>
            }
            span="half"
            status={readinessFor("PlannedInvitationWithinNoticeWindow")}
            extraStatuses={[readinessFor("PriorInformationCallOffered")]}
          />
        )}
        {isQualificationScheme(publicContext?.procedureType) && (
          <SummaryField
            label={t("The qualification system")}
            value={
              publicContext?.qualificationScheme ? (
                <Text>
                  {`${displayDate(publicContext.qualificationScheme.validFrom)} – ${displayDate(
                    publicContext.qualificationScheme.validTo,
                  )}`}
                </Text>
              ) : (
                <EmptyValue text={t("Not set")} />
              )
            }
            span="half"
            status={readinessFor("QualificationSchemeValidityPresent")}
            extraStatuses={[
              readinessFor("QualificationSchemeValidityOrdered"),
              readinessFor("ProcedureRequiresUtilitiesRegulation"),
            ]}
          />
        )}
        {!withoutCompetition && (
        <SummaryField
          label={t("Tender validity period")}
          value={
            schemeCompetition
              ? t("Does not apply to a competition under a qualification system")
              : isTwoStageProcedure(publicContext?.procedureType)
              ? t("Set when the invitation to tender is sent")
              : validityMonths != null
                ? `${validityMonths} ${t("month", { count: validityMonths })}`
                : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("TenderValidityMonthsSet")}
        />
        )}
        {!withoutCompetition && (
        <SummaryField
          label={t("Submission language")}
          value={
            submissionLanguages.length > 0
              ? submissionLanguages.map((code) => t(LANGUAGE_LABELS[code] ?? code)).join(", ")
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("SubmissionLanguagesSet")}
          extraStatuses={[readinessFor("SubmissionLanguagesValid")]}
        />
        )}
      </SummaryFieldGroup>
    </SummarySection>
  );
};
